'use client';

import React, { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

interface Differentiator {
  icon: React.ReactNode;
  title: string;
  description: string;
  highlight: string;
}

const differentiators: Differentiator[] = [
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
    title: 'Client-Focused Approach',
    description: 'We place your business objectives at the center of everything we do. Our consultants invest time to deeply understand your organization, culture, and goals before recommending any solution.',
    highlight: 'Your success is our success',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    title: 'Proven Methodologies',
    description: 'Our consulting frameworks are built on industry best practices, refined through real-world engagements. We apply structured, repeatable processes that deliver consistent, reliable outcomes.',
    highlight: 'Structured for reliability',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'Cross-Industry Expertise',
    description: 'Our consultants bring experience across multiple industries and functional domains. This breadth of knowledge enables us to apply cross-industry insights that create unique competitive advantages.',
    highlight: 'Diverse sector knowledge',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
      </svg>
    ),
    title: 'Tailored Solutions',
    description: 'We reject one-size-fits-all approaches. Every engagement begins with a thorough discovery process to ensure our recommendations are precisely calibrated to your specific situation and context.',
    highlight: 'Custom-fit every time',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
    title: 'Results-Driven',
    description: 'We measure our success by the tangible outcomes we deliver for your business. Our engagements are structured around clear KPIs, milestones, and measurable deliverables from day one.',
    highlight: 'Measurable impact always',
  },
];

const processSteps = [
  { step: '01', title: 'Discovery', description: 'Deep-dive into your business, challenges, and objectives through structured interviews and analysis.' },
  { step: '02', title: 'Strategy', description: 'Develop a tailored strategic roadmap with clear priorities, timelines, and success metrics.' },
  { step: '03', title: 'Implementation', description: 'Execute the strategy with expert guidance, change management support, and continuous monitoring.' },
  { step: '04', title: 'Optimization', description: 'Measure outcomes, refine approaches, and ensure sustainable long-term value delivery.' },
];

export default function WhyChooseUsSection() {
  const { ref, inView } = useInView();
  const { ref: processRef, inView: processInView } = useInView();

  return (
    <section
      id="why-us"
      className="py-24 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0A1526 0%, #160F46 100%)',
      }}
    >
      {/* Background pattern */}
      <div
        className="absolute inset-0 pointer-events-none opacity-10"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(66, 132, 245, 0.5) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }}
      />
      <div
        className="absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(66, 132, 245, 0.15) 0%, transparent 70%)',
          transform: 'translate(30%, -30%)',
        }}
      />
      <div
        className="absolute bottom-0 left-0 w-96 h-96 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(0, 189, 165, 0.1) 0%, transparent 70%)',
          transform: 'translate(-30%, 30%)',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section header */}
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-4"
            style={{
              background: 'rgba(66, 132, 245, 0.2)',
              border: '1px solid rgba(66, 132, 245, 0.4)',
              color: '#60A5FA',
            }}
          >
            Why Choose Us
          </div>
          <h2
            className="font-bold text-white mb-4"
            style={{ fontSize: 'clamp(28px, 4vw, 44px)' }}
          >
            The Pluto7 Consulting
            <span
              className="block"
              style={{
                background: 'linear-gradient(135deg, #4284F5, #60A5FA, #00BDA5)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Difference
            </span>
          </h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            We combine deep expertise, proven methodologies, and genuine partnership to deliver
            consulting outcomes that create lasting business value.
          </p>
        </div>

        {/* Differentiators grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {differentiators.map((item, i) => (
            <div
              key={i}
              className={`p-7 rounded-card group transition-all duration-300 hover:-translate-y-1 ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                transitionDelay: `${i * 80}ms`,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = 'rgba(66, 132, 245, 0.1)';
                (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(66, 132, 245, 0.3)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = 'rgba(255, 255, 255, 0.05)';
                (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                style={{ background: 'rgba(66, 132, 245, 0.2)', color: '#60A5FA' }}
              >
                {item.icon}
              </div>
              <h3 className="text-white font-bold text-lg mb-3">{item.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">{item.description}</p>
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{
                  background: 'rgba(0, 189, 165, 0.15)',
                  color: '#00BDA5',
                  border: '1px solid rgba(0, 189, 165, 0.2)',
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                {item.highlight}
              </div>
            </div>
          ))}
        </div>

        {/* Process section */}
        <div
          ref={processRef}
          className={`transition-all duration-700 ${
            processInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-center text-2xl font-bold text-white mb-12">
            Our Engagement Process
          </h3>
          <div className="grid md:grid-cols-4 gap-6 relative">
            {/* Connecting line */}
            <div
              className="hidden md:block absolute top-10 left-1/4 right-1/4 h-0.5 pointer-events-none"
              style={{ background: 'linear-gradient(90deg, rgba(66,132,245,0.3), rgba(66,132,245,0.6), rgba(66,132,245,0.3))' }}
            />

            {processSteps.map((step, i) => (
              <div
                key={i}
                className={`text-center transition-all duration-700 ${
                  processInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 relative"
                  style={{
                    background: 'linear-gradient(135deg, #4284F5, #21427B)',
                    boxShadow: '0 0 30px rgba(66, 132, 245, 0.3)',
                  }}
                >
                  <span className="text-white font-black text-xl">{step.step}</span>
                </div>
                <h4 className="text-white font-bold text-lg mb-3">{step.title}</h4>
                <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
